import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

type Variant = 'primary' | 'ghost' | 'link' | 'linkMuted';
type Size = 'default' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const base =
  'inline-flex items-center justify-center gap-2 font-medium transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-55';

const variants: Record<Variant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 rounded-md',
  ghost:
    'bg-transparent border border-input text-foreground hover:bg-muted rounded-md',
  link: 'text-primary hover:underline underline-offset-2 p-0 bg-transparent',
  linkMuted:
    'text-muted-foreground hover:underline underline-offset-2 p-0 bg-transparent',
};

const sizes: Record<Size, string> = {
  default: 'h-10 px-4 py-2 text-sm',
  lg: 'h-12 px-6 text-base',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'default', type = 'button', ...rest }, ref) => {
    const isLink = variant === 'link' || variant === 'linkMuted';
    return (
      <button
        ref={ref}
        type={type}
        className={cn(base, variants[variant], !isLink && sizes[size], className)}
        {...rest}
      />
    );
  },
);
Button.displayName = 'Button';

export default Button;
